import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListRuleSchemasResult, type PreviewPriceResult } from '@whinerp/client';
import {
  EmptyState,
  ErrorState,
  Num,
  PageHeading,
  Panel,
  PricePreview,
  RuleBuilder,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import type { RuleNode, RuleSchema } from '@whinerp/contracts/rules';
import { formatCount, formatMoney } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { ruleBuilderLabels } from './rule-labels.js';

/**
 * SC-42 Pricelist editor.
 *
 * Rules on the left, a preview on the right that takes a sample customer,
 * product and quantity - and names the rule that won. The naming is the
 * screen's reason to exist: a person building pricing rules is not asking
 * "what is the price", they are asking "which of my rules is going to
 * decide it", and only one of those questions can be answered by a number.
 */

function PricelistScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();

  const [rule, setRule] = useState<RuleNode>({
    all: [{ field: 'quantity', operator: 'gte', value: '10' }],
  });
  const [quantity, setQuantity] = useState('10');
  const [productId, setProductId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);

  const schemas = useQuery<ListRuleSchemasResult, ApiError>({
    queryKey: ['rule-schemas'],
    queryFn: () => api.listRuleSchemas({}),
  });

  const products = useQuery({
    queryKey: ['products', ''],
    queryFn: () => api.listProducts({ query: {} }),
  });

  const accounts = useQuery({
    queryKey: ['accounts-for-pricing'],
    queryFn: () => api.listAccounts({ query: { limit: 25 } }),
  });

  const chosenProduct = productId ?? products.data?.products[0]?.productId ?? null;

  const preview = useQuery<PreviewPriceResult, ApiError>({
    queryKey: ['price-preview', chosenProduct, accountId, quantity],
    queryFn: () =>
      api.previewPrice({
        body: {
          productId: chosenProduct ?? '',
          quantity,
          ...(accountId ? { accountId } : {}),
        },
      }),
    enabled: Boolean(chosenProduct) && /^\d+(\.\d+)?$/.test(quantity),
  });

  const schema = useMemo<RuleSchema | null>(() => {
    const found = schemas.data?.schemas.find((entry) => entry.key === 'pricelist');
    return (found as RuleSchema | undefined) ?? null;
  }, [schemas.data]);

  if (schemas.isError) {
    return <PricelistError error={schemas.error} retry={() => void schemas.refetch()} />;
  }
  if (schemas.isPending || !schema) return <PricelistLoading />;

  return (
    <div data-component="PricelistEditor" className="flex w-full flex-col gap-4">
      <PageHeading>{t('pricelist.title')}</PageHeading>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[65%_35%]">
        <Panel data-region="rules">
          <div className="flex flex-col gap-3">
            <SectionLabel>{t('pricelist.rules')}</SectionLabel>
            {/* The one builder, given the pricing schema. */}
            <RuleBuilder
              schema={schema}
              value={rule}
              onChange={setRule}
              labels={ruleBuilderLabels(t)}
              matchCount={null}
            />
          </div>
        </Panel>

        <Panel data-region="preview">
          <div className="flex flex-col gap-3">
            <SectionLabel>{t('pricelist.preview')}</SectionLabel>

            <label className="flex flex-col gap-1 text-dense text-ink">
              {t('pricelist.product')}
              <select
                data-preview-product
                value={chosenProduct ?? ''}
                onChange={(event) => setProductId(event.target.value)}
                className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
              >
                {(products.data?.products ?? []).map((product) => (
                  <option key={product.productId} value={product.productId}>
                    {product.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-dense text-ink">
              {t('pricelist.customer')}
              <select
                data-preview-account
                value={accountId ?? ''}
                onChange={(event) => setAccountId(event.target.value || null)}
                className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
              >
                <option value="">{t('field.choose')}</option>
                {(accounts.data?.rows ?? []).map((account) => (
                  <option key={account.accountId} value={account.accountId}>
                    {account.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-dense text-ink">
              {t('pricelist.quantity')}
              <input
                data-preview-quantity
                inputMode="decimal"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
              />
            </label>

            {preview.isError ? (
              <ErrorState
                title={t('state.error')}
                body={preview.error.message}
                onRetry={() => void preview.refetch()}
              />
            ) : (
              <PricePreview
                price={
                  preview.data
                    ? formatMoney(preview.data.unitPrice, format, {
                        currency: preview.data.currency,
                      })
                    : null
                }
                explanation={preview.data?.explanation ?? null}
                ruleApplied={Boolean(preview.data?.ruleId)}
                labels={{
                  price: t('pricelist.preview'),
                  winningRule: t('pricelist.winning'),
                  listPrice: t('pricelist.listPrice'),
                  alsoMatched: t('pricelist.rules'),
                  notPriced: t('rule.matchingUnknown'),
                }}
              />
            )}
          </div>
        </Panel>
      </div>

      <Num className="text-meta text-ink-meta">
        {t('rule.summary', {
          values: { count: formatCount(countConditions(rule), format), outcome: schema.outcome },
        })}
      </Num>
    </div>
  );
}

function countConditions(node: RuleNode): number {
  if ('all' in node) return node.all.reduce((n, child) => n + countConditions(child as RuleNode), 0);
  if ('any' in node) return node.any.reduce((n, child) => n + countConditions(child as RuleNode), 0);
  if ('none' in node) return node.none.reduce((n, child) => n + countConditions(child as RuleNode), 0);
  return 1;
}

function PricelistEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Tag"
      title={t('pricelist.empty.title')}
      body={t('pricelist.empty.body')}
      primary={{ label: t('empty.action.addRule'), icon: 'Plus', href: '/setup' }}
    />
  );
}

function PricelistLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-lg" />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Skeleton className="h-block-xl w-full" />
        <Skeleton className="h-block-xl w-full" />
      </div>
    </div>
  );
}

function PricelistError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Pricing rules could not be loaded"
      body={error instanceof Error ? error.message : 'They did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-42',
  route: '/pricelists/:id',
  surface: 'page',
  title: 'Pricelist editor',
  features: ['F-162'],
  entitlement: 'F-162',
  reachedBy: [{ kind: 'setup', section: 'pricing' }],
  components: ['RuleBuilder', 'PricePreview'],
  elements: ['PricelistEditor'],
  // Resolved, not written down. This was `{ id: 'default' }` - a literal that
  // is not a uuid and matched no row, so every render check counted a screen
  // that was showing its error state (P-7).
  sampleRecord: { table: { name: 'pricelist', idColumn: 'pricelist_id' }, param: 'id' },
  states: {
    empty: PricelistEmpty,
    loading: PricelistLoading,
    error: PricelistError,
  },
  component: PricelistScreen,
});

export default screen;
